import React, { useMemo } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
    useProject,
    useProjectStats,
    useUpdateProject,
} from "../../api/projects";
import { ProjectFunding } from "../../types/projects";

const ProjectDetails: React.FC = () => {
    const params = useParams();
    const navigate = useNavigate();
    const projectId = params.projectId ? Number(params.projectId) : null;

    const {
        data: projectResponse,
        isLoading,
        isError,
        error,
        refetch: refetchProject,
    } = useProject(projectId);
    const {
        data: statsResponse,
        isLoading: statsLoading,
        refetch: refetchStats,
    } = useProjectStats(projectId);
    const updateProject = useUpdateProject();

    const project: ProjectFunding | undefined = projectResponse?.data;
    const stats = statsResponse?.data;

    const progressPercent = useMemo(() => {
        if (!project) {
            return 0;
        }
        return Math.min(100, project.progress_percent);
    }, [project]);

    const handleToggleSubscriptions = async () => {
        if (!project) {
            return;
        }
        try {
            await updateProject.mutateAsync({
                id: project.id,
                is_subscription_active: !project.is_subscription_active,
            });
            refetchProject();
            refetchStats();
        } catch (err: any) {
            window.alert(
                err?.response?.data?.message ||
                    err?.message ||
                    "Не удалось обновить статус подписки."
            );
        }
    };

    if (!projectId) {
        return (
            <div className="p-6 text-red-500">Некорректный идентификатор проекта.</div>
        );
    }

    if (isLoading) {
        return (
            <div className="p-6 text-gray-500 dark:text-gray-300">
                Загрузка данных проекта...
            </div>
        );
    }

    if (isError || !project) {
        return (
            <div className="p-6 text-red-500">
                {(error as any)?.message || "Проект не найден."}
            </div>
        );
    }

    return (
        <div className="p-6 space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <button
                        onClick={() => navigate(-1)}
                        className="mb-3 inline-flex items-center text-sm text-blue-600 hover:text-blue-700 transition"
                    >
                        ← Назад
                    </button>
                    <h1 className="text-3xl font-semibold text-gray-900 dark:text-white">
                        {project.name}
                    </h1>
                    <p className="mt-2 text-gray-600 dark:text-gray-300 max-w-3xl">
                        {project.short_description || "Краткое описание отсутствует."}
                    </p>
                </div>
                <div className="flex space-x-3">
                    <Link
                        to={`/dashboard/projects/${project.id}/gallery`}
                        className="px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition"
                    >
                        Галерея
                    </Link>
                    <Link
                        to={`/dashboard/projects/${project.id}/subscribers`}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                    >
                        Подписчики и платежи
                    </Link>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white dark:bg-gray-900 shadow rounded-lg p-4">
                    <div className="text-sm text-gray-500 mb-1">Цель сбора</div>
                    <div className="text-2xl font-bold text-gray-900 dark:text-white">
                        {project.target_amount} USD
                    </div>
                    <div className="mt-2 text-sm text-gray-500">
                        Сборов: {project.collected_amount} USD
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-900 shadow rounded-lg p-4">
                    <div className="text-sm text-gray-500 mb-1">Прогресс</div>
                    <div className="text-2xl font-bold text-gray-900 dark:text-white">
                        {progressPercent.toFixed(1)}%
                    </div>
                    <div className="mt-2 h-2 bg-gray-200 dark:bg-gray-700 rounded">
                        <div
                            className="h-2 bg-blue-500 rounded"
                            style={{ width: `${progressPercent}%` }}
                        ></div>
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-900 shadow rounded-lg p-4">
                    <div className="text-sm text-gray-500 mb-1">Подписки</div>
                    <div className="text-2xl font-bold text-gray-900 dark:text-white">
                        {stats?.totals.active_subscriptions ?? 0}
                    </div>
                    <div className="mt-2 text-sm text-gray-500">
                        Всего: {stats?.totals.total_subscriptions ?? 0}
                    </div>
                </div>
            </div>

            <div className="bg-white dark:bg-gray-900 shadow rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                        Управление проектом
                    </h2>
                    <button
                        onClick={handleToggleSubscriptions}
                        className={`px-4 py-2 rounded-lg text-white transition ${
                            project.is_subscription_active
                                ? "bg-red-500 hover:bg-red-600"
                                : "bg-blue-600 hover:bg-blue-700"
                        }`}
                    >
                        {project.is_subscription_active
                            ? "Остановить списания"
                            : "Возобновить списания"}
                    </button>
                </div>

                <div className="space-y-3 text-sm text-gray-700 dark:text-gray-300">
                    <div>
                        <span className="font-semibold">Минимальная подписка:</span>{" "}
                        {project.min_subscription_amount} USD
                    </div>
                    <div>
                        <span className="font-semibold">Максимальный коэффициент:</span>{" "}
                        {project.max_coefficient}
                    </div>
                    <div>
                        <span className="font-semibold">Доля дивидендов:</span>{" "}
                        {project.dividend_pool_percent}% от прибыли проекта
                    </div>
                    <div>
                        <span className="font-semibold">Старт проекта:</span>{" "}
                        {project.launch_date
                            ? new Date(project.launch_date).toLocaleString()
                            : "ещё не запущен"}
                    </div>
                    <div>
                        <span className="font-semibold">Описание:</span>
                        <div className="mt-1 whitespace-pre-wrap">
                            {project.description || "Описание не заполнено."}
                        </div>
                    </div>
                </div>
            </div>

            <div className="bg-white dark:bg-gray-900 shadow rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                        Топ участников
                    </h2>
                    <div className="text-sm text-gray-500">
                        Всего с учётом ручных платежей:{" "}
                        {stats?.totals.total_paid_amount ?? project.collected_amount} USD
                    </div>
                </div>
                {statsLoading && (
                    <div className="text-gray-500 dark:text-gray-300">
                        Загрузка статистики...
                    </div>
                )}
                {!statsLoading && (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gray-50 dark:bg-gray-800">
                                <tr>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Пользователь
                                    </th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Сумма взносов
                                    </th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Доля дивидендов
                                    </th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Коэффициент
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                                {stats?.top_subscriptions.length ? (
                                    stats.top_subscriptions.map((subscription) => (
                                        <tr key={subscription.id}>
                                            <td className="px-4 py-2">
                                                <div className="text-sm font-semibold text-gray-900 dark:text-white">
                                                    {subscription.user?.name ||
                                                        subscription.user?.email ||
                                                        `ID: ${subscription.user_id}`}
                                                </div>
                                                {subscription.user?.email && (
                                                    <div className="text-xs text-gray-500">
                                                        {subscription.user.email}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-200">
                                                {subscription.total_paid_amount} USD
                                            </td>
                                            <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-200">
                                                {subscription.dividend_share_percent}%
                                            </td>
                                            <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-200">
                                                x{subscription.coefficient}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td
                                            colSpan={4}
                                            className="px-4 py-3 text-center text-gray-500"
                                        >
                                            Подписчики ещё не оформляли платежи.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ProjectDetails;

